import { useState, useEffect, memo, useRef } from 'react';
import logger from '@widgets/logger';
import { Typography } from '@components/Typography';
import InfoSnackbar from '@components/InfoSnackbar';
import { useTranslation } from '@hooks';
import { Trans } from 'react-i18next';
import { getServiceWorkerRegistration } from '@services/serviceWorker';
import { BRAND_TEAL } from '@constants/colors';

const ServiceWorkerUpdates = memo(() => {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const refreshingRef = useRef(false);

    useEffect(() => {
        const hideSWUpdates = JSON.parse(localStorage.getItem('hideSWUpdates'));
        if (hideSWUpdates) return;

        const checkAndUpdateServiceWorker = async () => {
            const registration = await getServiceWorkerRegistration();
            if (!registration) return;

            if (registration.waiting) {
                setIsOpen(true);
            }
            registration.addEventListener('updatefound', () => {
                if (registration.installing) {
                    // INFO: wait until the new Service worker is actually installed (ready to take over)
                    registration.installing.addEventListener('statechange', () => {
                        if (registration.waiting) {
                            // INFO: if there's an existing controller (previous Service Worker), show the prompt
                            if (navigator.serviceWorker.controller) {
                                setIsOpen(true);
                            } else {
                                // INFO: otherwise it's the first install, nothing to do
                                logger.info('Service Worker initialized for the first time');
                            }
                        }
                    });
                }
            });
            navigator.serviceWorker.addEventListener('controllerchange', () => {
                if (!refreshingRef.current) {
                    window.location.reload();
                    refreshingRef.current = true;
                }
            });
        };

        checkAndUpdateServiceWorker();
    }, []);

    const handleClose = (_, reason) => {
        if (reason === 'clickaway') return;
        setIsOpen(false);
        // INFO: if true - don't show snackbar again
        localStorage.setItem('hideSWUpdates', true);
    };

    const onUpdate = async () => {
        const registration = await getServiceWorkerRegistration();
        if (registration?.waiting) {
            // INFO: let waiting Service Worker know it should became active
            registration.waiting.postMessage('SKIP_WAITING');
        }
        setIsOpen(false);
    };

    return (
        <InfoSnackbar
            isOpen={isOpen}
            handleClose={handleClose}
            onClick={onUpdate}
            message={
                <Typography variant="body2" sx={{ color: BRAND_TEAL, textAlign: 'center' }}>
                    <Trans t={t} i18nKey="SettingsProfile.serviceWorkerUpdate">
                        <b
                            style={{ textDecoration: 'underline', cursor: 'pointer', textTransform: 'uppercase' }}
                            onClick={onUpdate}
                        >
                            {{ refresh: t('common.refresh') }}
                        </b>
                    </Trans>
                </Typography>
            }
        />
    );
});

ServiceWorkerUpdates.displayName = 'ServiceWorkerUpdates';

export default ServiceWorkerUpdates;
